// Builds and sends stake-program transactions from the wallet's token accounts.

import {
  Connection,
  PublicKey,
  SystemProgram,
  SYSVAR_RENT_PUBKEY,
  Transaction,
  TransactionInstruction,
} from '@solana/web3.js';
import { Buffer } from 'buffer';
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  findAssociatedTokenAddress,
  signAndSendTransaction,
} from '../tokens';
import { TokenAccount } from '../tokens/hooks';
import { Wallet } from '../wallet';
import { describeError } from './errors';
import { Pool, claimIx, stakeIx, unstakeIx } from './program';

export interface MintHolding {
  pubkey: PublicKey;
  amount: bigint;
}

/** The wallet's accounts for `mint`, largest first. */
export function holdingsForMint(accounts: TokenAccount[] | undefined, mint: PublicKey): MintHolding[] {
  const address = mint.toBase58();
  return (accounts ?? [])
    .filter((account) => account.account.data.parsed.info.mint === address)
    .map((account) => ({
      pubkey: account.pubkey,
      amount: BigInt(account.account.data.parsed.info.tokenAmount.amount),
    }))
    .filter((holding) => holding.amount > 0n)
    .sort((a, b) => (a.amount > b.amount ? -1 : a.amount < b.amount ? 1 : 0));
}

export function totalHoldings(holdings: MintHolding[]): bigint {
  return holdings.reduce((sum, holding) => sum + holding.amount, 0n);
}

function createAtaIx(payer: PublicKey, owner: PublicKey, mint: PublicKey, tokenProgram: PublicKey): TransactionInstruction {
  return new TransactionInstruction({
    programId: ASSOCIATED_TOKEN_PROGRAM_ID,
    keys: [
      { pubkey: payer, isSigner: true, isWritable: true },
      { pubkey: findAssociatedTokenAddress(owner, mint, tokenProgram), isSigner: false, isWritable: true },
      { pubkey: owner, isSigner: false, isWritable: false },
      { pubkey: mint, isSigner: false, isWritable: false },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: tokenProgram, isSigner: false, isWritable: false },
      { pubkey: SYSVAR_RENT_PUBKEY, isSigner: false, isWritable: false },
    ],
    data: Buffer.alloc(0),
  });
}

/** Destination for payouts: the owner's ATA, created in the same transaction if missing. */
async function payoutAccount(
  connection: Connection,
  owner: PublicKey,
  pool: Pool,
  holdings: MintHolding[],
): Promise<{ address: PublicKey; create: TransactionInstruction | null }> {
  const ata = findAssociatedTokenAddress(owner, pool.mint, pool.tokenProgram);
  if (holdings.some((holding) => holding.pubkey.equals(ata))) return { address: ata, create: null };
  const info = await connection.getAccountInfo(ata);
  if (info) return { address: ata, create: null };
  return { address: ata, create: createAtaIx(owner, owner, pool.mint, pool.tokenProgram) };
}

/** One Stake per source account until `amount` is covered; top-ups make this a single position. */
export function buildStake(owner: PublicKey, pool: Pool, holdings: MintHolding[], amount: bigint): Transaction {
  if (amount <= 0n) throw new Error('amount must be positive');
  if (totalHoldings(holdings) < amount) throw new Error('insufficient balance');
  const tx = new Transaction();
  let remaining = amount;
  for (const holding of holdings) {
    if (remaining === 0n) break;
    const part = holding.amount < remaining ? holding.amount : remaining;
    tx.add(stakeIx(owner, pool.mint, holding.pubkey, pool.tokenProgram, part));
    remaining -= part;
  }
  return tx;
}

export async function buildClaim(connection: Connection, owner: PublicKey, pool: Pool, holdings: MintHolding[]): Promise<Transaction> {
  const { address, create } = await payoutAccount(connection, owner, pool, holdings);
  const tx = new Transaction();
  if (create) tx.add(create);
  return tx.add(claimIx(owner, pool.mint, address, pool.tokenProgram));
}

export async function buildUnstake(
  connection: Connection,
  owner: PublicKey,
  pool: Pool,
  holdings: MintHolding[],
  amount: bigint | null,
): Promise<Transaction> {
  const { address, create } = await payoutAccount(connection, owner, pool, holdings);
  const tx = new Transaction();
  if (create) tx.add(create);
  return tx.add(unstakeIx(owner, pool.mint, address, pool.tokenProgram, amount));
}

/** Simulates, then signs, sends and confirms. Program errors surface before any signature prompt. */
export async function sendStakingTransaction(connection: Connection, wallet: Wallet, tx: Transaction): Promise<string> {
  tx.feePayer = wallet.publicKey;
  const simulation = await connection.simulateTransaction(tx);
  if (simulation.value.err) throw new Error(describeError(simulation.value.err));
  try {
    const signature: string = await signAndSendTransaction(connection, tx, wallet, []);
    await connection.confirmTransaction(signature, 'confirmed');
    return signature;
  } catch (err) {
    throw new Error(describeError(err));
  }
}
